/**
 * Inspection Status Card
 *
 * Shows current inspection status, timeline, and action buttons.
 * Only visible when a job has been submitted for inspection.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardList,
  CheckCircle,
  Clock,
  Calendar,
  AlertTriangle,
  Loader2
} from "lucide-react";
import type { InspectionStatus } from "@/pages/JobDetail";

interface InspectionStatusCardProps {
  status: InspectionStatus;
  submittedAt?: string | null;
  scheduledAt?: string | null;
  completedAt?: string | null;
  readinessScore?: number;
  blockerCount?: number;
  checklistCompletion?: number;
  onMarkVerified?: () => void;
  isMarkingVerified?: boolean;
}

export function InspectionStatusCard({
  status,
  submittedAt,
  scheduledAt,
  completedAt,
  readinessScore = 0,
  blockerCount = 0,
  checklistCompletion = 0,
  onMarkVerified,
  isMarkingVerified = false,
}: InspectionStatusCardProps) {
  // Determine if "Mark as Verified" should be enabled
  const canMarkVerified =
    status === 'pending_verification' &&
    readinessScore >= 80 &&
    blockerCount === 0 &&
    checklistCompletion >= 100;

  const getStatusConfig = (status: InspectionStatus) => {
    switch (status) {
      case 'pending_verification':
        return {
          label: 'Pending Verification',
          description: 'Complete the pre-inspection checklist to proceed',
          color: 'border-amber-400 text-amber-600 bg-amber-50 dark:bg-amber-950',
          icon: Clock,
        };
      case 'verified':
        return {
          label: 'Ready for Inspection',
          description: 'All requirements met. Schedule your inspection.',
          color: 'border-blue-400 text-blue-600 bg-blue-50 dark:bg-blue-950',
          icon: CheckCircle,
        };
      case 'scheduled':
        return {
          label: 'Inspection Scheduled',
          description: scheduledAt
            ? `Scheduled for ${new Date(scheduledAt).toLocaleDateString()}`
            : 'Waiting for inspection date',
          color: 'border-purple-400 text-purple-600 bg-purple-50 dark:bg-purple-950',
          icon: Calendar,
        };
      case 'passed':
        return {
          label: 'Inspection Passed',
          description: completedAt
            ? `Passed on ${new Date(completedAt).toLocaleDateString()}`
            : 'Congratulations! Your inspection passed.',
          color: 'border-green-400 text-green-600 bg-green-50 dark:bg-green-950',
          icon: CheckCircle,
        };
      case 'failed':
        return {
          label: 'Inspection Failed',
          description: 'Review the findings and address any issues',
          color: 'border-red-400 text-red-600 bg-red-50 dark:bg-red-950',
          icon: AlertTriangle,
        };
      default:
        return {
          label: 'Unknown Status',
          description: '',
          color: 'border-gray-400 text-gray-600 bg-gray-50 dark:bg-gray-950',
          icon: ClipboardList,
        };
    }
  };

  const config = getStatusConfig(status);
  const StatusIcon = config.icon;

  return (
    <Card className={`${config.color} border-2`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StatusIcon className="h-5 w-5" />
            Inspection Status
          </div>
          <Badge variant="outline" className={config.color}>
            {config.label}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm">{config.description}</p>

        {/* Timeline */}
        <div className="space-y-2 text-sm">
          {submittedAt && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 opacity-60" />
              <span className="opacity-80">Submitted:</span>
              <span>{new Date(submittedAt).toLocaleDateString()}</span>
            </div>
          )}
          {scheduledAt && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 opacity-60" />
              <span className="opacity-80">Scheduled:</span>
              <span>{new Date(scheduledAt).toLocaleDateString()}</span>
            </div>
          )}
          {completedAt && (
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 opacity-60" />
              <span className="opacity-80">Completed:</span>
              <span>{new Date(completedAt).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Readiness Stats - only show for pending_verification */}
        {status === 'pending_verification' && (
          <div className="grid grid-cols-3 gap-3 pt-2 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold">{readinessScore}%</p>
              <p className="text-xs opacity-70">Readiness</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{blockerCount}</p>
              <p className="text-xs opacity-70">Blockers</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{checklistCompletion}%</p>
              <p className="text-xs opacity-70">Checklist</p>
            </div>
          </div>
        )}

        {/* Actions */}
        {status === 'pending_verification' && onMarkVerified && (
          <div className="pt-2">
            <Button
              onClick={onMarkVerified}
              disabled={!canMarkVerified || isMarkingVerified}
              className="w-full"
            >
              {isMarkingVerified ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Verified
                </>
              )}
            </Button>
            {!canMarkVerified && (
              <p className="text-xs text-center mt-2 opacity-70">
                {readinessScore < 80 && 'Readiness score must be at least 80%. '}
                {blockerCount > 0 && 'Resolve all blockers. '}
                {checklistCompletion < 100 && 'Complete the checklist.'}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
